import { useEffect, useMemo, useState } from 'react';
import type { Sessao } from '../../auth';
import { ErroDeArea } from '../../design/Estados';
import { ROTULO_DE_FORA_DO_COTAC, gerarCotac, lerCandidatosAoCotac } from '../../faltas';
import type { CandidatosAoCotac, FiltroDoPainel } from '../../faltas';
import { Gaveta } from './Gaveta';

/**
 * FALTAS → GERAR TXT P/ SMARTPED (dono, 03/11/2026). A falta não tem quantidade: cada produto vem
 * com o nº de faltas do período e a pessoa ajusta ou tira antes de baixar. Quem ficou de fora
 * aparece com o motivo, nada some calado. O arquivo é montado no servidor.
 */

function baixarTexto(nome: string, conteudo: string) {
  const url = URL.createObjectURL(new Blob([conteudo], { type: 'text/plain' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = nome;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1500);
}

const diaCurto = (d: string) => d.split('-').reverse().join('/');
const itensTexto = (n: number) => `${String(n)} ${n === 1 ? 'item' : 'itens'}`;

export function GerarCotac({
  sessao,
  filtro,
  aoFechar,
}: {
  sessao: Sessao;
  filtro: FiltroDoPainel;
  aoFechar: () => void;
}) {
  // A lista fica presa aos filtros de quando a gaveta abriu.
  const [filtroFixo] = useState(filtro);
  const [dados, setDados] = useState<CandidatosAoCotac | null>(null);
  const [erro, setErro] = useState(false);
  const [tentativa, setTentativa] = useState(0);
  const [qtd, setQtd] = useState<Readonly<Record<string, string>>>({});
  const [tirados, setTirados] = useState<ReadonlySet<string>>(new Set());
  const [baixando, setBaixando] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; texto: string } | null>(null);

  useEffect(() => {
    let vivo = true;
    setErro(false);
    void lerCandidatosAoCotac(filtroFixo).then((r) => {
      if (!vivo) return;
      if (!r.ok) {
        setErro(true);
        return;
      }
      setDados(r.dados.candidatos);
      setQtd(
        Object.fromEntries(r.dados.candidatos.entram.map((c) => [c.productId, String(c.faltas)])),
      );
    });
    return () => {
      vivo = false;
    };
  }, [filtroFixo, tentativa]);

  const itens = useMemo(
    () => (dados?.entram ?? []).filter((c) => !tirados.has(c.productId)),
    [dados, tirados],
  );
  const qtdValida = (id: string) => Number(qtd[id] ?? 0) >= 1;
  const podeBaixar = itens.length > 0 && !baixando && itens.every((c) => qtdValida(c.productId));

  const baixar = async () => {
    setBaixando(true);
    setMsg(null);
    const r = await gerarCotac(
      sessao,
      itens.map((c) => ({ produtoId: c.productId, quantidade: Number(qtd[c.productId]) })),
    );
    setBaixando(false);
    if (r.ok) {
      const a = r.dados.arquivo;
      baixarTexto(a.nome, a.conteudo);
      setMsg({
        ok: true,
        texto: `${a.nome} baixado: ${String(a.linhas)} ${a.linhas === 1 ? 'produto' : 'produtos'}, ${String(a.unidades)} ${a.unidades === 1 ? 'unidade' : 'unidades'}.`,
      });
    } else
      setMsg({
        ok: false,
        texto: r.falha.tipo === 'invalido' ? r.falha.mensagem : 'Não foi possível gerar o arquivo.',
      });
  };

  return (
    <Gaveta
      titulo="Gerar TXT p/ SmartPed"
      subtitulo={`Faltas de ${diaCurto(filtroFixo.de)} a ${diaCurto(filtroFixo.ate)}, com os filtros da tela`}
      aoFechar={aoFechar}
      largura={620}
      rodape={
        <>
          <button type="button" className="enc-botao-sec" onClick={aoFechar}>
            Fechar
          </button>
          <button
            type="button"
            className="enc-botao"
            disabled={!podeBaixar}
            onClick={() => void baixar()}
          >
            {baixando ? 'Gerando...' : `Baixar COTAC (${itensTexto(itens.length)})`}
          </button>
        </>
      }
    >
      {erro ? (
        <ErroDeArea oQue="os produtos em falta" aoTentar={() => setTentativa((t) => t + 1)} />
      ) : !dados ? (
        <div className="enc-lista-esq enc-esqueleto">
          <i />
          <i />
          <i />
        </div>
      ) : (
        <div className="ft-cotac">
          {msg && <p className={msg.ok ? 'enc-faixa-ok' : 'enc-faixa-aviso'}>{msg.texto}</p>}
          {dados.entram.length === 0 ? (
            <p className="enc-dica">Nenhum produto sem estoque com esses filtros.</p>
          ) : itens.length === 0 ? (
            <p className="enc-dica">Todos os produtos foram tirados da lista.</p>
          ) : (
            <ul className="ft-cotac-lista">
              {itens.map((c) => (
                <li key={c.productId}>
                  <span>
                    <b>{c.descricao}</b>
                    <small>
                      {c.fabricante ?? 'Fabricante não informado'} · {String(c.faltas)}{' '}
                      {c.faltas === 1 ? 'falta' : 'faltas'}
                    </small>
                  </span>
                  <input
                    inputMode="numeric"
                    value={qtd[c.productId] ?? ''}
                    aria-label={`Quantidade de ${c.descricao}`}
                    aria-invalid={!qtdValida(c.productId)}
                    maxLength={7}
                    onChange={(e) =>
                      setQtd({ ...qtd, [c.productId]: e.target.value.replace(/\D/g, '') })
                    }
                  />
                  <button
                    type="button"
                    className="enc-link"
                    onClick={() => setTirados(new Set(tirados).add(c.productId))}
                  >
                    Tirar
                  </button>
                </li>
              ))}
            </ul>
          )}
          {tirados.size > 0 && (
            <button type="button" className="enc-link" onClick={() => setTirados(new Set())}>
              Devolver {tirados.size === 1 ? 'o que tirei' : `os ${String(tirados.size)} que tirei`}
            </button>
          )}
          {dados.deFora.length > 0 && (
            <details className="ft-cotac-fora">
              <summary>Ficaram de fora: {String(dados.deFora.length)}</summary>
              <ul className="ft-cotac-lista">
                {dados.deFora.map((c) => (
                  <li key={c.productId}>
                    <span>
                      <b>{c.descricao}</b>
                      <small>
                        {ROTULO_DE_FORA_DO_COTAC[c.motivo]}
                        {c.motivo === 'COM_ESTOQUE' && c.estoqueAtual !== null
                          ? `: ${c.estoqueAtual.toLocaleString('pt-BR', { maximumFractionDigits: 3 })} un.`
                          : ''}
                      </small>
                    </span>
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}
    </Gaveta>
  );
}
